/**
 * CareerPage -- Manage career entries and their variant banks.
 *
 * Lists career entries with company, title, dates. Expandable sections
 * for editing description, responsibilities, and managing variants.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { MonthYearPicker } from '../../components/ui/MonthYearPicker';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { useAiAssist } from '../../hooks/useAiAssist';
import { aiApi } from '../../services/ai.api';
import { logger } from '../../services/logger.client';
import type { CareerEntry, CareerEntryVariant } from '../../types/cms.types';
import type {
  EnrichCompanyData,
  GenerateContentData,
  ExtractAchievementsRequest,
  ExtractAchievementsData,
} from '../../types/ai.types';

const log = logger.child({ module: 'CareerPage' });

/* ------------------------------------------------------------------ */
/*  Variant Bank                                                      */
/* ------------------------------------------------------------------ */

interface VariantBankProps {
  entryId: string;
  variants: CareerEntryVariant[];
}

function VariantBank({ entryId, variants }: VariantBankProps) {
  const { createVariant, updateVariant, deleteVariant } = useContentActions();

  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  function resetForm() {
    setLabel('');
    setDescription('');
    setResponsibilities('');
    setEditingId(null);
    setShowForm(false);
  }

  function handleEdit(variant: CareerEntryVariant) {
    setLabel(variant.label);
    setDescription(variant.description ?? '');
    setResponsibilities((variant.responsibilities ?? []).join('\n'));
    setEditingId(variant.id);
    setShowForm(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;

    const data: Partial<CareerEntryVariant> = {
      label: label.trim(),
      description: description.trim() || null,
      responsibilities: responsibilities
        .split('\n')
        .map((r) => r.trim())
        .filter(Boolean),
    };

    if (editingId) {
      await updateVariant(entryId, editingId, data);
    } else {
      await createVariant(entryId, data);
    }
    resetForm();
  }

  async function handleDelete(variantId: string) {
    if (!window.confirm('Delete this variant?')) return;
    await deleteVariant(entryId, variantId);
  }

  return (
    <div className="cms-variant-bank">
      <div className="cms-variant-bank__header">
        <h4 className="cms-variant-bank__title">Variants</h4>
        {!showForm && (
          <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            + Add Variant
          </Button>
        )}
      </div>

      {variants.length === 0 && !showForm && (
        <p className="list-empty">No variants yet.</p>
      )}

      {variants.map((v) => (
        <div key={v.id} className="cms-variant-card">
          <div className="cms-variant-card__header">
            <span className="cms-variant-card__label">{v.label}</span>
            <div className="cms-variant-card__actions">
              <Button variant="secondary" size="sm" onClick={() => handleEdit(v)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={() => void handleDelete(v.id)}>
                Delete
              </Button>
            </div>
          </div>
          {v.description && (
            <p className="cms-variant-card__desc">{v.description}</p>
          )}
        </div>
      ))}

      {showForm && (
        <form className="cms-variant-form" onSubmit={handleSubmit} noValidate>
          <Input
            label="Variant Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Management Focus"
            required
          />
          <Textarea
            label="Description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Variant-specific description..."
          />
          <Textarea
            label="Responsibilities (one per line)"
            rows={4}
            value={responsibilities}
            onChange={(e) => setResponsibilities(e.target.value)}
            placeholder="Led a team of 8 engineers&#10;Delivered project on time..."
          />
          <div className="cms-page__actions-row">
            <Button variant="secondary" size="sm" onClick={resetForm} type="button">
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Career Entry Editor                                               */
/* ------------------------------------------------------------------ */

interface CareerEntryEditorProps {
  entry: CareerEntry;
  variants: CareerEntryVariant[];
}

function CareerEntryEditor({ entry, variants }: CareerEntryEditorProps) {
  const { updateCareerEntry, deleteCareerEntry } = useContentActions();

  const [company, setCompany] = useState(entry.company);
  const [title, setTitle] = useState(entry.title);
  const [entryType, setEntryType] = useState(entry.entry_type);
  const [startDate, setStartDate] = useState(entry.start_date);
  const [endDate, setEndDate] = useState(entry.end_date ?? '');
  const [isCurrent, setIsCurrent] = useState(entry.is_current);
  const [location, setLocation] = useState(entry.location ?? '');
  const [description, setDescription] = useState(entry.base_description ?? '');
  const [responsibilities, setResponsibilities] = useState(
    (entry.base_responsibilities ?? []).join('\n'),
  );
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });

  /* ---- AI Assist: Company enrichment ---- */
  const companyAi = useAiAssist<{ company_name: string }, EnrichCompanyData>({
    apiCall: (input) => aiApi.enrichCompany(input),
    onAccept: (data) => {
      if (data.address) setLocation(data.address);
      if (data.description && !description) setDescription(data.description);
    },
  });

  /* ---- AI Assist: Description generation ---- */
  const descriptionAi = useAiAssist<
    { type: 'job_description'; context: { company: string; title: string; start_date: string; end_date?: string; responsibilities?: string[] } },
    GenerateContentData
  >({
    apiCall: (input) => aiApi.generateContent(input),
    onAccept: (data) => {
      setDescription(data.content);
    },
  });

  /* ---- AI Assist: Achievement extraction ---- */
  const achievementsAi = useAiAssist<
    ExtractAchievementsRequest,
    ExtractAchievementsData
  >({
    apiCall: (input) => aiApi.extractAchievements(input),
    onAccept: (data) => {
      setResponsibilities(data.achievements.join('\n'));
    },
  });

  async function handleSave() {
    await executeSave(() =>
      updateCareerEntry(entry.id, {
        company,
        title,
        entry_type: entryType,
        start_date: startDate,
        end_date: endDate || null,
        is_current: isCurrent,
        location: location || null,
        base_description: description || null,
        base_responsibilities: responsibilities
          .split('\n')
          .map((r) => r.trim())
          .filter(Boolean),
      }),
    );
  }

  async function handleDelete() {
    if (!window.confirm('Delete this career entry? This cannot be undone.'))
      return;
    await deleteCareerEntry(entry.id);
  }

  return (
    <Panel>
      <Panel.Header>
        {entry.company} - {entry.title}
      </Panel.Header>
      <Panel.Content>
        <div className="editor-field-group">
          <div className="experience-entry__row">
            <div className="input-wrapper">
              <div className="ai-label-row">
                <label className="input-label" htmlFor={`company-${entry.id}`}>Company</label>
                <AiAssistButton
                  onClick={() => void companyAi.trigger({ company_name: company })}
                  isLoading={companyAi.isLoading}
                  disabled={!company.trim()}
                  label="Enrich company data with AI"
                />
              </div>
              <input
                id={`company-${entry.id}`}
                className="input"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company"
              />
              <AiSuggestionPanel
                suggestion={companyAi.suggestion as Record<string, unknown> | null}
                error={companyAi.error}
                isLoading={companyAi.isLoading}
                onAccept={companyAi.accept}
                onDismiss={companyAi.dismiss}
              />
            </div>
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="experience-entry__row">
            <div className="input-wrapper">
              <label className="input-label">Type</label>
              <select
                className="input"
                value={entryType}
                onChange={(e) =>
                  setEntryType(e.target.value as 'job' | 'contract')
                }
              >
                <option value="job">Job</option>
                <option value="contract">Contract</option>
              </select>
            </div>
            <Input
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City, State"
            />
          </div>
          <div className="experience-entry__row">
            <MonthYearPicker
              label="Start Date"
              value={startDate}
              onChange={(v) => {
                setStartDate(v);
                // If end date is earlier than new start date, clear it
                if (endDate && v && endDate < v) setEndDate('');
              }}
              placeholder="Select start month"
            />
            <MonthYearPicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              placeholder="Select end month"
              allowPresent
              isPresent={isCurrent}
              onPresentChange={(p) => {
                setIsCurrent(p);
                if (p) setEndDate('');
              }}
              minValue={startDate || undefined}
              disabled={isCurrent}
            />
          </div>
          <div className="input-wrapper">
            <div className="ai-label-row">
              <label className="input-label" htmlFor={`desc-${entry.id}`}>
                Description
              </label>
              <AiAssistButton
                onClick={() =>
                  void descriptionAi.trigger({
                    type: 'job_description',
                    context: {
                      company,
                      title,
                      start_date: startDate,
                      end_date: endDate || undefined,
                      responsibilities: responsibilities
                        .split('\n')
                        .map((r) => r.trim())
                        .filter(Boolean),
                    },
                  })
                }
                isLoading={descriptionAi.isLoading}
                disabled={!company.trim() || !title.trim()}
                label="Generate description with AI"
              />
            </div>
            <textarea
              id={`desc-${entry.id}`}
              className="input input--textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe responsibilities and accomplishments..."
            />
            <AiSuggestionPanel
              suggestion={descriptionAi.suggestion ? descriptionAi.suggestion.content : null}
              error={descriptionAi.error}
              isLoading={descriptionAi.isLoading}
              onAccept={descriptionAi.accept}
              onDismiss={descriptionAi.dismiss}
            />
          </div>
          <div className="input-wrapper">
            <div className="ai-label-row">
              <label className="input-label" htmlFor={`resp-${entry.id}`}>
                Responsibilities (one per line)
              </label>
              <AiAssistButton
                onClick={() =>
                  void achievementsAi.trigger({
                    raw_notes: responsibilities,
                    job_title: title || undefined,
                    company: company || undefined,
                  })
                }
                isLoading={achievementsAi.isLoading}
                disabled={responsibilities.trim().length < 10}
                label="Extract achievements with AI"
              />
            </div>
            <textarea
              id={`resp-${entry.id}`}
              className="input input--textarea"
              rows={4}
              value={responsibilities}
              onChange={(e) => setResponsibilities(e.target.value)}
              placeholder="Led engineering team&#10;Designed API architecture..."
            />
            <AiSuggestionPanel
              suggestion={achievementsAi.suggestion as Record<string, unknown> | null}
              error={achievementsAi.error}
              isLoading={achievementsAi.isLoading}
              onAccept={achievementsAi.accept}
              onDismiss={achievementsAi.dismiss}
              renderSuggestion={(data) => {
                const { achievements } = data as ExtractAchievementsData;
                return (
                  <ul className="ai-suggestion-panel__list">
                    {achievements.map((item, idx) => (
                      <li key={idx} className="ai-suggestion-panel__list-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
          </div>
          <div className="cms-page__actions-row">
            {saveError && (
              <span className="editor-page__save-msg editor-page__save-msg--error">
                {saveError}
              </span>
            )}
            {saveMessage && (
              <span className="editor-page__save-msg">{saveMessage}</span>
            )}
            <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
              Delete
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              loading={isSaving}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Variant Bank */}
        <VariantBank entryId={entry.id} variants={variants} />
      </Panel.Content>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function CareerPage() {
  const careerEntries = useContentStore((s) => s.careerEntries);
  const variants = useContentStore((s) => s.variants);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const { fetchCareerEntries, createCareerEntry, fetchVariants } = useContentActions();

  const [showCreate, setShowCreate] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    log.info('CareerPage mounted');
    void fetchCareerEntries();
  }, [fetchCareerEntries]);

  useEffect(() => {
    for (const entry of careerEntries) {
      if (!variants[entry.id]) {
        void fetchVariants(entry.id);
      }
    }
  }, [careerEntries, variants, fetchVariants]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newCompany.trim() || !newTitle.trim()) return;

    await createCareerEntry({
      company: newCompany.trim(),
      title: newTitle.trim(),
      entry_type: 'job',
      start_date: '',
      is_current: false,
      display_order: careerEntries.length,
    });

    setNewCompany('');
    setNewTitle('');
    setShowCreate(false);
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Career Entries</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowCreate(!showCreate)}
        >
          + Add Entry
        </Button>
      </header>

      {showCreate && (
        <Panel>
          <Panel.Header>New Career Entry</Panel.Header>
          <Panel.Content>
            <form className="editor-field-group" onSubmit={handleCreate} noValidate>
              <div className="experience-entry__row">
                <Input
                  label="Company"
                  value={newCompany}
                  onChange={(e) => setNewCompany(e.target.value)}
                  placeholder="Acme Corp"
                  required
                />
                <Input
                  label="Title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Software Engineer"
                  required
                />
              </div>
              <div className="cms-page__actions-row">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Create
                </Button>
              </div>
            </form>
          </Panel.Content>
        </Panel>
      )}

      <LoadingState loading={isLoading} error={error}>
        {careerEntries.length === 0 ? (
          <div className="list-empty">
            No career entries yet. Click '+ Add Entry' to get started.
          </div>
        ) : (
          <div className="cms-page__body">
            {careerEntries.map((entry) => (
              <CareerEntryEditor
                key={entry.id}
                entry={entry}
                variants={variants[entry.id] ?? []}
              />
            ))}
          </div>
        )}
      </LoadingState>
    </div>
  );
}
